import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { ButtonViewProps } from '@/design-system/components/buttons/UniButton';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { Dropdown } from '@/design-system/components/dropdown';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniInput } from '@/design-system/components/inputs';
import { H6, TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { useBottomsheet } from '@/devtools/screens_deprecated/useBottomsheet.hook';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { LayoutContext } from 'contexts/LayoutContext';
import { Pressable, ScrollView, SxProp, View } from 'dripsy';
import { isString } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'store/store';
import { RootStackRoutes } from 'types';
import { BillPaymentStackRoutes } from '@/features/bill-payment/payment.navigator';
import { UnionPayStackRoutes } from '@/features/bill-payment/screens/union-pay/type';
import { RemittancePurposeSelectFilters, FundSourceSelectFilters } from '@/features/bill-payment/screens/union-pay/options';
import { PageHeaderModalNative } from '@/features/bill-payment/screens/components/PageHeaderModalNative';
import { UnionType } from '@/features/bill-payment/enum';

export interface InformationNativeScreenProps {
  onBack?: (flag?: boolean) => void;
  transferPeer?: any;
  wallet?: any;
  calcFee?: any;
  transferPeerCountFlag?: boolean;
  unionType?: UnionType.PAYMENT | UnionType.ACCOUNT;
  refresh?: () => void;
  navigationNavigate?: string;
  navigationScreen?: string;
}

export const InformationNativeScreen: React.FC<InformationNativeScreenProps> = (props) => {
  const {
    onBack,
    transferPeer,
    wallet,
    calcFee,
    transferPeerCountFlag = true,
    unionType,
    refresh,
    navigationNavigate = RootStackRoutes.BillPayment,
    navigationScreen = BillPaymentStackRoutes.UnionPay
  } = props;
  const { t } = useTranslation('bill-payment-union-pay');
  const navigation = useNavigation();
  const navigationAny = useNavigation<any>();
  const { bottomsheetRef, onOpen, onClose } = useBottomsheet();
  const { bottomsheetRef: bottomSheetOTPRef, onOpen: onOpenOTP, onClose: onCloseOTP } = useBottomsheet();
  const isFocused = useIsFocused();
  const store = useStore();
  const layout = useContext(LayoutContext);

  const [buttonProps, setButtonProps] = useState<Partial<ButtonViewProps>>({
    size: 'default',
    variant: ButtonVariant.primary,
    label: 'Button Label'
  });

  const handleChangeButtonProps = useCallback((key: keyof ButtonViewProps, value: ButtonViewProps[keyof ButtonViewProps]) => {
    setButtonProps((prev) => ({ ...prev, [key]: value }));
  }, []);

  const [accountData, setAccountData] = useState(transferPeer);

  const [beneficiaryInformation, setBeneficiaryInformation] = useState<{
    account: string | undefined;
    purpose: string | undefined;
    fundSource: string | undefined;
    remark: string | undefined;
  }>({
    account: undefined,
    purpose: undefined,
    fundSource: undefined,
    remark: undefined
  });
  useEffect(() => {
    setBeneficiaryInformation({ ...beneficiaryInformation, account: transferPeer });
  }, []);
  const [errorMsg, setErrorMsg] = useState<{ account: string | undefined; purpose: string | undefined; fundSource: string | undefined }>({
    account: undefined,
    purpose: undefined,
    fundSource: undefined
  });
  const validateSubmit = () => {
    if (!beneficiaryInformation.account) {
      setErrorMsg({ ...errorMsg, account: t('unionPay::payment-instruction.field-required') });
    } else if (!beneficiaryInformation.purpose) {
      setErrorMsg({ ...errorMsg, purpose: t('unionPay::payment-instruction.field-required') });
    } else if (!beneficiaryInformation.fundSource) {
      setErrorMsg({ ...errorMsg, fundSource: t('unionPay::payment-instruction.field-required') });
    } else {
      navigationAny.navigate(navigationNavigate, {
        screen: navigationScreen,
        params: { screen: UnionPayStackRoutes.Confirmation, onBack, wallet, calcFee, beneficiaryInformation, unionType, refresh }
      });
    }
  };

  return (
    <View sx={sxStyles.container}>
      <PageHeaderModalNative>
        <View sx={{ px: '$4', my: '$5' }}>
          <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
            <UniIconButton
              icon="arrowLeft"
              options={{ iconStyles: { themeColor: '$white' } }}
              onPress={() => {
                onBack && onBack();
              }}></UniIconButton>
          </View>
          <View sx={{ textAlign: 'center', width: '100%' }}>
            <TextTitle bold sx={{ color: '$white' }}>
              {t('unionPay::payment-instruction.glossary.unionpay')}
            </TextTitle>
          </View>
          <View sx={{ position: 'absolute', top: 0, right: 12, zIndex: 2 }}>
            <UniIconButton
              icon="rightIcon"
              onPress={() => {
                navigationAny.navigate(navigationNavigate, {
                  screen: navigationScreen,
                  params: { screen: UnionPayStackRoutes.UnionPayMore }
                });
              }}></UniIconButton>
          </View>
        </View>
      </PageHeaderModalNative>
      <ScrollView sx={sxStyles.scrollViewWrapper} contentContainerStyle={{ flexGrow: 1 }} showsVerticalScrollIndicator={false}>
        <View sx={layout.dimensions.height >= 728 ? { height: layout.dimensions.height - 168 } : {}}>
          <View sx={sxStyles.topHeader}>
            <Icon name="stepTwo" height={40} width={40} />
            <View sx={sxStyles.textMb}>
              <H6 bold>{t('unionPay::payment-instruction.title--recipient-information')}</H6>
              <TextBody>{t('unionPay::payment-instruction.text--debit-amount')}</TextBody>
            </View>
          </View>
          <View sx={{ gap: 20 }}>
            <View sx={{ gap: 4 }}>
              <TextCaption semiBold>{t('unionPay::payment-instruction.text--unionpay-account')}</TextCaption>
              <Pressable
                disabled={!transferPeerCountFlag}
                onPress={() => {
                  navigationAny.navigate(navigationNavigate, {
                    screen: navigationScreen,
                    params: {
                      screen: UnionPayStackRoutes.SelectAccount,
                      onBack,
                      onSelect: (data: any) => {
                        setBeneficiaryInformation({ ...beneficiaryInformation, account: data });
                        setAccountData(data);
                      }
                    }
                  });
                }}>
                <View sx={{ ...sxStyles.account, ...(accountData ? { flexDirection: 'column', py: '$2' } : {}) }}>
                  <View
                    sx={{
                      justifyContent: 'space-between',
                      display: 'flex',
                      width: '100%',
                      flexDirection: 'row',
                      alignItems: 'center'
                    }}>
                    {accountData ? (
                      <View sx={{ gap: '$1' }}>
                        <TextBody semiBold sx={{ color: '$textPrimary' }}>
                          {accountData?.accountLabel}
                        </TextBody>
                        <TextBody sx={{ color: '$textPrimary' }}>{accountData?.accountNumber}</TextBody>
                      </View>
                    ) : (
                      <TextCaption sx={{ color: '$borderUpload' }}>{t('unionPay::payment-instruction.text--select-account')}</TextCaption>
                    )}
                    <Icon name="arrowRight" />
                  </View>
                </View>
              </Pressable>
              <TextCaption secondary>{t('unionPay::payment-instruction.text--make-sure-accurate-information')}</TextCaption>
            </View>
            <View sx={{ ...sxStyles.dropdown, zIndex: 100 }}>
              <View sx={{ zIndex: 9999 }}>
                <Dropdown
                  label={t('unionPay::payment-instruction.text--purpose')}
                  placeholder={t('unionPay::payment-instruction.text--select-purpose')}
                  options={RemittancePurposeSelectFilters(t)}
                  sx={{ width: '100%' }}
                  dropdownSx={{ maxHeight: 210 }}
                  onValueChange={(value) => {
                    handleChangeButtonProps('size', value as ButtonViewProps['size']);
                    setBeneficiaryInformation({ ...beneficiaryInformation, purpose: isString(value) ? value : '' });
                  }}
                />
              </View>
              <View sx={{ zIndex: 9888 }}>
                <Dropdown
                  label={t('unionPay::payment-instruction.text--fund-source')}
                  placeholder={t('unionPay::payment-instruction.text--select-source')}
                  options={FundSourceSelectFilters(t)}
                  sx={{ width: '100%' }}
                  dropdownSx={{ maxHeight: 120 }}
                  onValueChange={(value) => {
                    handleChangeButtonProps('size', value as ButtonViewProps['size']);
                    setBeneficiaryInformation({ ...beneficiaryInformation, fundSource: isString(value) ? value : '' });
                  }}
                />
              </View>
              <UniInput
                labelText={t('unionPay::payment-instruction.text--remarks-optional')}
                multiline
                textAlignVertical="top"
                sx={{ height: 68, alignItems: 'flex-start' }}
                inputSx={{ height: 68, lineHeight: 34 }}
                onValueChange={(value) => setBeneficiaryInformation({ ...beneficiaryInformation, remark: value })}
              />
            </View>
          </View>
        </View>
        <View sx={{ ...sxStyles.btnView, zIndex: 10 }}>
          <UniButton
            sx={{ height: 44 }}
            labelSx={{ color: '$balck' }}
            label={t('unionPay::payment-instruction.button--submit')}
            disabled={beneficiaryInformation.account && beneficiaryInformation.purpose && beneficiaryInformation.fundSource ? false : true}
            onPress={validateSubmit}
          />
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: '$backgroundPrimary',
    width: '100%',
    height: '100%'
  },
  header: {
    flexDirection: 'column'
    // padding: '$4'
  },
  tab: {
    backgroundColor: 'transparent',
    width: '98%'
  },
  body: {
    flex: 1,
    zIndex: 10
  },
  scrollViewWrapper: {
    pt: '$5',
    pb: '$2',
    px: '$4'
  },
  account: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    py: '$5',
    px: '$4',
    borderWidth: 1,
    borderColor: '#DCDCDC',
    borderRadius: 5
  },
  topHeader: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: '$5',
    gap: '$2'
  },
  textMb: {
    textAlign: 'center',
    gap: 8
  },
  btnView: {
    width: '100%',
    left: 0,
    bottom: 0,
    pt: '$5'
  },
  dropdown: {
    gap: '$5'
  }
};
